import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Typography, Paper, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle,
  CircularProgress, Alert, Box, Chip, IconButton, Tooltip, Grid, Card, CardContent,
  Divider, Stack, InputAdornment, Tab, Tabs, Avatar
} from '@mui/material';
import {
  Check, Close, Refresh, Phone, CardMembership, Description, Search,
  Visibility, HelpOutline, Info, History, ZoomIn, Receipt, CalendarToday
} from '@mui/icons-material';
import { getImageUrl } from '../utils/imageUrl';

/* Lightbox Document / Receipt Image */
const DocImage = ({ src, label, onZoom }) => {
  if (!src) return (
    <Box sx={{ width: 130, height: 90, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px dashed #cbd5e1' }}>
      <Description sx={{ color: '#94a3b8', fontSize: 24 }} />
      <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>{label}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not Uploaded</Typography>
    </Box>
  );

  const fullUrl = getImageUrl(src);

  return (
    <Tooltip title={`Click to zoom ${label}`}>
      <Box
        sx={{ position: 'relative', cursor: 'pointer', width: 130, '&:hover .zoom-overlay': { opacity: 1 } }}
        onClick={() => onZoom(fullUrl, label)}
      >
        <Box
          component="img"
          src={fullUrl}
          alt={label}
          sx={{
            width: 130, height: 90, objectFit: 'cover', borderRadius: 2,
            border: '1px solid #cbd5e1', display: 'block', transition: 'all 0.2s'
          }}
        />
        <Box className="zoom-overlay" sx={{
          position: 'absolute', top: 0, left: 0, width: 130, height: 90, borderRadius: 2,
          backgroundColor: 'rgba(0,0,0,0.4)', color: 'white', display: 'flex',
          alignItems: 'center', justifyContent: 'center', opacity: 0, transition: '0.2s opacity'
        }}>
          <ZoomIn />
        </Box>
        <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 0.5, color: '#64748b', fontWeight: 600 }} noWrap>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const PendingMemberships = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState('all');

  // Dialog & Modal state
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' | 'reject' | 'request_info'
  const [inputReason, setInputReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);

  const fetchPendingMemberships = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/admin/pending-memberships');
      setDrivers(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching pending membership applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingMemberships();
  }, [fetchPendingMemberships]);

  const handleOpenAction = (driver, type) => {
    setSelectedDriver(driver);
    setActionType(type);
    setInputReason('');
    setActionDialogOpen(true);
  };

  const handleExecuteAction = async () => {
    if (!selectedDriver) return;
    setProcessing(true);
    setError('');
    setSuccessMsg('');

    try {
      if (actionType === 'approve') {
        await axios.put(`/admin/approve-membership/${selectedDriver._id}`, { note: inputReason });
        setSuccessMsg(`Membership activated for ${selectedDriver.name}.`);
      } else if (actionType === 'reject') {
        if (!inputReason.trim()) {
          setError('Rejection reason is required.');
          setProcessing(false);
          return;
        }
        await axios.put(`/admin/reject-membership/${selectedDriver._id}`, { reason: inputReason });
        setSuccessMsg(`Membership request rejected for ${selectedDriver.name}.`);
      } else if (actionType === 'request_info') {
        if (!inputReason.trim()) {
          setError('Details are required when requesting information.');
          setProcessing(false);
          return;
        }
        await axios.put(`/admin/request-info-membership/${selectedDriver._id}`, { details: inputReason });
        setSuccessMsg(`Information request sent to ${selectedDriver.name}.`);
      }

      setActionDialogOpen(false);
      setOpenDetailModal(false);
      fetchPendingMemberships();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process membership action.');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusChip = (d) => {
    const status = d.membershipStatus || 'pending';
    switch (status) {
      case 'active':
        return <Chip label="Active / Approved" color="success" size="small" icon={<Check />} />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" icon={<Close />} />;
      case 'info_requested':
        return <Chip label="Info Requested" color="warning" size="small" icon={<HelpOutline />} />;
      default:
        return <Chip label="Pending Activation" color="info" size="small" icon={<Info />} />;
    }
  };

  const filteredDrivers = drivers.filter((d) => {
    const status = d.membershipStatus || 'pending';
    const matchesTab = filterTab === 'all' ? true : status === filterTab;
    const matchesSearch =
      d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.phoneNumber?.includes(searchTerm) ||
      d.membershipPlanName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <Box sx={{ p: 1 }}>
      {/* Header Banner */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f172a' }}>
            Pending Membership Approvals 💳
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review driver subscription applications, plan details, payment proofs, and activate memberships.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchPendingMemberships} disabled={loading}>
          Refresh List
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Filter Tabs & Search Bar */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Tabs
              value={filterTab}
              onChange={(e, val) => setFilterTab(val)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
            >
              <Tab label={`All (${drivers.length})`} value="all" />
              <Tab label={`Pending (${drivers.filter(d => !d.membershipStatus || d.membershipStatus === 'pending').length})`} value="pending" />
              <Tab label={`Info Requested (${drivers.filter(d => d.membershipStatus === 'info_requested').length})`} value="info_requested" />
              <Tab label={`Rejected (${drivers.filter(d => d.membershipStatus === 'rejected').length})`} value="rejected" />
            </Tabs>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by driver name, phone, or plan name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Cards Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredDrivers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CardMembership sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No Membership Applications</Typography>
          <Typography variant="body2" color="text.disabled">
            {searchTerm ? 'No applications match your search query.' : 'All pending driver memberships have been processed.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredDrivers.map((driver) => (
            <Grid item xs={12} key={driver._id}>
              <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Box sx={{ height: 4, bgcolor: driver.membershipStatus === 'info_requested' ? '#f59e0b' : driver.membershipStatus === 'rejected' ? '#ef4444' : '#16a34a' }} />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {/* Driver Profile */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#16a34a', width: 48, height: 48 }}>
                          {driver.name ? driver.name.charAt(0) : 'D'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">{driver.name || 'Unnamed Driver'}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            <Phone fontSize="inherit" sx={{ mr: 0.5 }} /> {driver.phoneNumber}
                          </Typography>
                          {getStatusChip(driver)}
                        </Box>
                      </Box>
                    </Grid>

                    {/* Membership Plan Details */}
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CardMembership fontSize="small" /> Selected Subscription Plan
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Stack spacing={0.8}>
                          <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                            {driver.membershipPlanName || 'Standard Plan'}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="action" /> Expiry Date: <strong>{driver.membershipExpiresAt ? new Date(driver.membershipExpiresAt).toLocaleDateString() : 'N/A'}</strong>
                          </Typography>
                        </Stack>
                      </Paper>

                      {driver.requestedInfoNote && (
                        <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                          <strong>Info Requested:</strong> {driver.requestedInfoNote}
                        </Alert>
                      )}
                      {driver.rejectionReason && (
                        <Alert severity="error" sx={{ mt: 1.5, py: 0.5, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                          <strong>Rejection Reason:</strong> {driver.rejectionReason}
                        </Alert>
                      )}
                    </Grid>

                    {/* Payment Proof / Receipt */}
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Receipt fontSize="small" /> Payment Verification Proof
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <DocImage src={driver.paymentProof || driver.membershipReceipt} label="Payment Receipt / Screenshot" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => { setSelectedDriver(driver); setOpenDetailModal(true); }}
                    >
                      View Details & History
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Check />}
                      onClick={() => handleOpenAction(driver, 'approve')}
                    >
                      Activate Membership
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<HelpOutline />}
                      onClick={() => handleOpenAction(driver, 'request_info')}
                    >
                      Request Info
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={() => handleOpenAction(driver, 'reject')}
                    >
                      Reject Application
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Detail Modal */}
      {selectedDriver && (
        <Dialog open={openDetailModal} onClose={() => setOpenDetailModal(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: '#16a34a' }}>{selectedDriver.name?.charAt(0)}</Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">{selectedDriver.name}</Typography>
                <Typography variant="caption" color="text.secondary">{selectedDriver.phoneNumber}</Typography>
              </Box>
            </Box>
            {getStatusChip(selectedDriver)}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>Driver Profile</Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2"><b>Driver Name:</b> {selectedDriver.name}</Typography>
                    <Typography variant="body2"><b>Phone Number:</b> {selectedDriver.phoneNumber}</Typography>
                    <Typography variant="body2"><b>Email:</b> {selectedDriver.email || 'N/A'}</Typography>
                    <Typography variant="body2"><b>License No:</b> {selectedDriver.licenseNumber || 'N/A'}</Typography>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f0fdf4' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="success.main" gutterBottom>Membership Details</Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2"><b>Plan Name:</b> {selectedDriver.membershipPlanName}</Typography>
                    <Typography variant="body2"><b>Requested Date:</b> {new Date(selectedDriver.createdAt || Date.now()).toLocaleDateString()}</Typography>
                    <Typography variant="body2"><b>Expiry Date:</b> {selectedDriver.membershipExpiresAt ? new Date(selectedDriver.membershipExpiresAt).toLocaleDateString() : 'N/A'}</Typography>
                  </Stack>
                </Paper>
              </Grid>

              {/* Documents */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>Payment Documents</Typography>
                <DocImage src={selectedDriver.paymentProof || selectedDriver.membershipReceipt} label="Payment Receipt / Screenshot" onZoom={(url, label) => setZoomImage({ url, title: label })} />
              </Grid>

              {/* History */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <History fontSize="small" /> Approval History & Audit Log
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                  {selectedDriver.approvalHistory && selectedDriver.approvalHistory.length > 0 ? (
                    <Stack spacing={1.5}>
                      {selectedDriver.approvalHistory.map((item, idx) => (
                        <Box key={idx} sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, borderLeft: '4px solid #16a34a' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                              Action: {item.action?.replace('_', ' ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{new Date(item.timestamp).toLocaleString()}</Typography>
                          </Box>
                          {item.reason && <Typography variant="body2" color="text.secondary">Note/Reason: {item.reason}</Typography>}
                          {item.requestedDetails && <Typography variant="body2" color="warning.main">Requested Details: {item.requestedDetails}</Typography>}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No prior approval history logged. Initial membership request.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, backgroundColor: '#f1f5f9' }}>
            <Button onClick={() => setOpenDetailModal(false)} color="inherit">Close</Button>
            <Button variant="outlined" color="warning" startIcon={<HelpOutline />} onClick={() => handleOpenAction(selectedDriver, 'request_info')}>
              Request Information
            </Button>
            <Button variant="outlined" color="error" startIcon={<Close />} onClick={() => handleOpenAction(selectedDriver, 'reject')}>
              Reject Application
            </Button>
            <Button variant="contained" color="success" startIcon={<Check />} onClick={() => handleOpenAction(selectedDriver, 'approve')}>
              Activate Membership
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' && 'Activate Driver Membership'}
          {actionType === 'reject' && 'Reject Membership Application'}
          {actionType === 'request_info' && 'Request Additional Information'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applicant: <strong>{selectedDriver?.name}</strong> ({selectedDriver?.membershipPlanName})
          </Typography>

          {actionType === 'approve' && (
            <TextField
              fullWidth
              label="Activation Note (Optional)"
              placeholder="e.g. Payment receipt verified successfully"
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
            />
          )}

          {actionType === 'reject' && (
            <TextField
              fullWidth
              required
              label="Rejection Reason"
              placeholder="e.g. Payment proof unverified or transaction ID invalid"
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
              error={!inputReason.trim()}
              helperText="State the reason for rejecting this membership."
            />
          )}

          {actionType === 'request_info' && (
            <TextField
              fullWidth
              required
              label="Requested Information & Instructions"
              placeholder="e.g. Please re-upload a clear copy of your payment receipt or transaction reference number."
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
              error={!inputReason.trim()}
              helperText="Specify what information or documents the applicant must submit."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setActionDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === 'approve' ? 'success' : actionType === 'reject' ? 'error' : 'warning'}
            onClick={handleExecuteAction}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={18} /> : actionType === 'approve' ? <Check /> : actionType === 'reject' ? <Close /> : <HelpOutline />}
          >
            Confirm {actionType.replace('_', ' ')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox Zoom */}
      {zoomImage && (
        <Dialog open={Boolean(zoomImage)} onClose={() => setZoomImage(null)} maxWidth="lg">
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {zoomImage.title}
            <IconButton onClick={() => setZoomImage(null)}><Close /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', p: 1 }}>
            <img src={zoomImage.url} alt={zoomImage.title} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default PendingMemberships;
